import moment from 'moment';
import { ICitizen } from '../Citizen';
import { isDate } from 'lodash';
import { ISiapUserInfo } from 'server/schema/user/siap/services/SiapGetUserInfo';
import { SiapContextualValues } from './SiapContextualValue';
import { MetadataType } from '../metadata/MetadataType';

export interface IContextualValue<T extends string> {
  type: T;
  /**
   * Se non specificato è stringa (MetadataType.Text)
   */
  datatype?: MetadataType;
  label: string;
  requiresAuth?: boolean;
  disableIfEmpty?: boolean;
  fromRequest(request: IContextualRequestBag): ExtractedContextualValue;
}

export const ContextualValue = {
  create<T extends string>(data: IContextualValue<T>): IContextualValue<T> {
    return data;
  },
  find<T extends string>(type: T): IContextualValue<T> {
    return ContextualValues.find(cv => cv.type === type) as IContextualValue<T>;
  },
  /**
   * Converte il valore contestuale in un valore utilizzabile all'interno
   * del validatore di Yup come default
   */
  convertForYup(type: string, value: any) {
    // Il codice fiscale deve essere sempre valido se arriva da SPID per
    // mantenere la compatibilità
    // TODO Rendere più omogeneo
    if (type === 'issuerFiscalCode') {
      return undefined;
    }

    return moment.isMoment(value) ? value.toDate() : value;
  }
};

/**
 * Parametri necessari per la compilazione dei metadati contestuali a partire
 * da una struttura simile alla richiesta.
 */
export interface IContextualRequestBag {
  citizen?: ICitizen | null | undefined;
  sentAt?: Date | null | undefined;
  code?: string | null | undefined;
  spidLevel?: string;
  siap?: ISiapUserInfo | null;
}

export function ContextualRequestBag(
  bag: IContextualRequestBag
): IContextualRequestBag {
  return bag;
}

/**
 * Il risultato di un valore contestuale può essere `undefined` in caso sia
 * un valore collegato a un cittadino, ma il cittadino non è autenticato
 */
export type ExtractedContextualValue =
  | moment.Moment
  | Date
  | string
  | number
  | undefined;

/**
 * Possibili valori contestuali e relativo estrattore
 */
export const ContextualValues = [
  ContextualValue.create({
    type: 'compiledDate',
    datatype: MetadataType.Date,
    label: 'Data di Compilazione',
    fromRequest: request => {
      return moment(request.sentAt ?? undefined)
        .tz('Europe/Rome')
        .format('DD/MM/YYYY');
    }
  }),
  ContextualValue.create({
    type: 'compiledTime',
    label: 'Ora di Compilazione',
    datatype: MetadataType.Text,
    fromRequest: request => {
      return moment(request.sentAt ?? undefined)
        .tz('Europe/Rome')
        .format('HH.mm');
    }
  }),
  ContextualValue.create({
    type: 'code',
    label: 'Codice della Richiesta',
    fromRequest: request => {
      return request.code ?? 'N.D.';
    }
  }),
  ContextualValue.create({
    type: 'issuerSpidLevel',
    label: 'Cittadino: Livello SPID',
    requiresAuth: true,
    fromRequest: request => {
      return request.spidLevel ?? 'N.D.';
    }
  }),
  ContextualValue.create({
    type: 'issuerName',
    label: 'Cittadino: Nome',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.name;
    }
  }),
  ContextualValue.create({
    type: 'issuerFamilyName',
    label: 'Cittadino: Cognome',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.familyName;
    }
  }),
  ContextualValue.create({
    type: 'issuerFullName',
    label: 'Cittadino: Nome completo',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.fullName;
    }
  }),
  ContextualValue.create({
    type: 'issuerPlaceOfBirth',
    label: 'Cittadino: Luogo di nascita',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.placeOfBirth;
    }
  }),
  ContextualValue.create({
    type: 'issuerCountyOfBirth',
    label: 'Cittadino: Provincia di nascita',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.countyOfBirth;
    }
  }),
  ContextualValue.create({
    type: 'issuerDateOfBirth',
    label: 'Cittadino: Data di nascita',
    datatype: MetadataType.Date,
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.dateOfBirth
        ? moment.tz(
            request.citizen.dateOfBirth,
            [moment.ISO_8601, 'YYYY-MM-DD'],
            'Europe/Rome'
          )
        : undefined;
    }
  }),
  ContextualValue.create({
    type: 'issuerGender',
    label: 'Cittadino: Sesso',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.gender;
    }
  }),
  ContextualValue.create({
    type: 'issuerFiscalCode',
    label: 'Cittadino: Codice Fiscale',
    requiresAuth: true,
    fromRequest: request => {
      return (
        request.citizen?.localFiscalNumber ?? request.citizen?.fiscalNumber
      );
    }
  }),
  ContextualValue.create({
    type: 'issuerVatCode',
    label: 'Cittadino: Partita IVA',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.ivaCode;
    }
  }),
  // TODO companyName
  ContextualValue.create({
    type: 'issuerRegisteredOffice',
    label: 'Cittadino: Sede legale',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.registeredOffice;
    }
  }),
  ContextualValue.create({
    type: 'issuerEmail',
    label: 'Cittadino: E-Mail',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.email;
    }
  }),
  ContextualValue.create({
    type: 'issuerMobilePhone',
    label: 'Cittadino: Numero di telefono mobile',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.mobilePhone;
    }
  }),
  ContextualValue.create({
    type: 'issuerSpidCode',
    label: 'Cittadino: SPID Code',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.spidCode;
    }
  }),
  ContextualValue.create({
    type: 'issuerDigitalAddress',
    label: 'Cittadino: Domicilio digitale',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.digitalAddress;
    }
  }),
  ContextualValue.create({
    type: 'issuerAddress',
    label: 'Cittadino: Domicilio fisico',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.address;
    }
  }),
  ContextualValue.create({
    type: 'issuerIdCard',
    label: "Cittadino: Documento d'identità",
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.idCard;
    }
  }),
  ContextualValue.create({
    type: 'issuerExpirationDate',
    label: 'Cittadino: Data di scadenza identità',
    datatype: MetadataType.Date,
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.expirationDate
        ? moment(request.citizen?.expirationDate)
            .tz('Europe/Rome')
            .format('DD/MM/YYYY')
        : undefined;
    }
  }),
  ContextualValue.create({
    type: 'issuerDomicileStreetAddress',
    label: 'Cittadino: Indirizzo di Domicilio',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.domicileStreetAddress;
    }
  }),
  ContextualValue.create({
    type: 'issuerDomicilePostalCode',
    label: 'Cittadino: CAP Domicilio',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.domicilePostalCode;
    }
  }),
  ContextualValue.create({
    type: 'issuerDomicileNation',
    label: 'Cittadino: Nazione Domicilio',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.domicileNation;
    }
  }),
  ContextualValue.create({
    type: 'issuerDomicileProvince',
    label: 'Cittadino: Provincia Domicilio',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.domicileProvince;
    }
  }),
  ContextualValue.create({
    type: 'issuerDomicileMunicipality',
    label: 'Cittadino: Comune Domicilio',
    requiresAuth: true,
    fromRequest: request => {
      return request.citizen?.domicileMunicipality;
    }
  }),
  // SIAB
  ...SiapContextualValues()
];

export type ContextualValueType = typeof ContextualValues extends Array<
  IContextualValue<infer T>
>
  ? T
  : never;

export function normalizeContextualValue(value: ExtractedContextualValue) {
  if (moment.isMoment(value)) return value.toISOString();
  return value;
}

/**
 * Si occupa di trasformare il valore contestuale in una stringa per la
 * composizione
 */
export function formatContextualValue(value: ExtractedContextualValue) {
  if (!value) return '';

  if (moment.isMoment(value)) {
    return value.format('DD/MM/YYYY');
  }

  if (isDate(value)) {
    return moment.tz(value, 'Europe/Rome').format('DD/MM/YYYY');
  }

  return value;
}
